import axios, { AxiosRequestConfig } from 'axios';
import { reportAgentQueryResult } from '../components/alliance/agent-access';
import {
  reportRequestFailure,
  reportRequestSuccess,
} from '../components/alliance/connection-monitor';

/** The JSON body of a request, or null (Amplify sends it as a string) */
const requestBody = (params: { config?: AxiosRequestConfig }) => {
  const data = params.config?.data;
  if (typeof data !== 'string') {
    return data ?? null;
  }
  try {
    return JSON.parse(data);
  } catch (error) {
    return null;
  }
};

/**
 * Report a request's outcome for the agent its body names (the
 * impersonated agent's number), for the noAgent screen. Searching agents
 * isn't acting for one, so /agent/search is skipped.
 */
const reportAgentAccess = (params: {
  config?: AxiosRequestConfig;
  error?: unknown;
}) => {
  if (/\/agent\/search$/.test(params.config?.url || '')) {
    return;
  }
  const body = requestBody({ config: params.config });
  if (body) {
    reportAgentQueryResult({ queryKey: [body], error: params.error });
  }
};

/**
 * Watch every API call. Amplify's API module sends its requests through the
 * default axios instance, so one response interceptor sees both the
 * react-query calls and the legacy useEffect ones (most of Dashboard). It feeds:
 *
 * - the connection banner (only network errors and 5xx count)
 * - the noAgent screen (403s vs. successes for the impersonated agent)
 *
 * Cancelled requests are ignored.
 */
export function installRequestMonitor() {
  axios.interceptors.response.use(
    (response) => {
      reportRequestSuccess();
      reportAgentAccess({ config: response.config });
      return response;
    },
    (error) => {
      if (!axios.isCancel(error)) {
        reportRequestFailure(error);
        reportAgentAccess({ config: error?.config, error });
      }
      return Promise.reject(error);
    },
  );
}
